import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { endpoints } from "@/lib/api";
import { todayISO, dateToISO } from "@/lib/utils-format";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CREW_COLORS = {
  "Crew A": "bg-primary/20 text-primary border-primary/40",
  "Crew B": "bg-secondary/20 text-secondary border-secondary/40",
  "Design": "bg-accent/30 text-amber-700 dark:text-amber-300 border-accent/40",
  "Solo": "bg-muted text-foreground border-border",
};

export default function CalendarPage() {
  const [jobs, setJobs] = useState([]);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { endpoints.list("jobs").then(r => setJobs(r.data)); }, []);

  const days = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const startDay = first.getDay();
    const total = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const jobsByDate = useMemo(() => {
    const map = {};
    jobs.forEach(j => {
      const k = (j.date || "").slice(0,10);
      if (!map[k]) map[k] = [];
      map[k].push(j);
    });
    return map;
  }, [jobs]);

  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const todayKey = todayISO();

  return (
    <Layout title="Calendar" subtitle="Schedule and crew assignments">
      <Card className="rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl font-semibold">{monthLabel}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1))} data-testid="cal-prev"><ChevronLeft className="size-4"/></Button>
            <Button variant="outline" onClick={()=>setCursor(new Date())} data-testid="cal-today">Today</Button>
            <Button variant="outline" size="icon" onClick={()=>setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1))} data-testid="cal-next"><ChevronRight className="size-4"/></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden text-sm" data-testid="calendar-grid">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
            <div key={d} className="bg-muted/50 p-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold text-center">{d}</div>
          ))}
          {days.map((d, i) => {
            const key = d ? dateToISO(d) : null;
            const cellKey = key || `empty-${i}`;
            const dayJobs = key ? (jobsByDate[key] || []) : [];
            const isToday = key === todayKey;
            return (
              <div key={cellKey} className={`bg-card min-h-[110px] p-2 ${isToday ? "ring-2 ring-primary ring-inset" : ""}`}>
                {d && (
                  <>
                    <div className={`text-xs font-semibold ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d.getDate()}</div>
                    <div className="space-y-1 mt-1">
                      {dayJobs.slice(0,3).map(j => (
                        <div key={j.id} className={`text-[10px] px-1.5 py-1 rounded border truncate ${CREW_COLORS[j.crew] || CREW_COLORS["Solo"]}`} title={`${j.client_name} — ${j.service}`}>
                          {j.time} {j.client_name?.split(" ")[0]}
                        </div>
                      ))}
                      {dayJobs.length > 3 && <div className="text-[10px] text-muted-foreground">+{dayJobs.length-3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-4 text-xs">
          {Object.entries(CREW_COLORS).map(([c, cls]) => (
            <div key={c} className="flex items-center gap-1.5"><span className={`size-3 rounded-sm border ${cls}`}/>{c}</div>
          ))}
        </div>
      </Card>
    </Layout>
  );
}
